import { useCallback, useEffect, useRef } from "react"
import { type AgGridReact } from "ag-grid-react"
import { AggregationMethod } from "~/components/reusable/AggregationSelector"
import ProductTableV2 from "~/components/planning/ProductTable/ProductTableV2"
import { ProductTableColumn } from "~/utils/productTableUtils"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { ProductTableType } from "~/constants/productTable"
import { SelectedVariantsProvider } from "~/context/SelectedVariantsProvider"

export default function SelectableVariantsTable({
  id,
  variants,
  loading = false,
  updateSelectedSkus,
  updateSelectedVariants,
}: {
  id: string
  variants: IVariantSummary[]
  loading?: boolean
  updateSelectedSkus?: (selectedSkus: string[]) => void
  updateSelectedVariants?: (selectedVariants: string[]) => void
}) {
  const gridRef = useRef<AgGridReact<IVariantSummary>>(null)
  const variantsRef = useRef(variants)
  useEffect(() => {
    variantsRef.current = variants
  }, [variants])

  const handleVariantsUpdated = useCallback(
    (selectedVariantIds: string[]) => {
      const selectedIds = new Set(selectedVariantIds)
      const filteredVariants = variantsRef.current.filter((v) => selectedIds.has(v.variantId))
      const skuIds = filteredVariants.flatMap((v) => v.skuIds)
      if (updateSelectedSkus) {
        updateSelectedSkus(skuIds)
      }
      if (updateSelectedVariants) {
        updateSelectedVariants(selectedVariantIds)
      }
    },
    [updateSelectedSkus, updateSelectedVariants],
  )

  return (
    <SelectedVariantsProvider<IVariantSummary> gridRef={gridRef}>
      <ProductTableV2
        id={id}
        gridRef={gridRef}
        type={ProductTableType.None}
        height="60vh"
        minHeight={200}
        variants={variants}
        initialAggregation={AggregationMethod.ProductName}
        defaultColumnIdsToDisplay={[
          ProductTableColumn.Variant,
          ProductTableColumn.SKU,
          ProductTableColumn.Size,
          ProductTableColumn.Brand,
          ProductTableColumn.Category,
          ProductTableColumn.Gender,
          ProductTableColumn.ProductType,
        ]}
        enableCellChangeFlash={false}
        selectableRows
        onSelectedVariantsChange={handleVariantsUpdated}
        loading={loading}
        enableSearch={true}
        enableFilter={true}
      />
    </SelectedVariantsProvider>
  )
}
